<template>
  <div v-if="activeBanners.length > 0" class="sub-banner" :style="{ background: currentBanner.bgColor || '#ff6b35' }">
    <div class="banner-container">
      <transition name="fade" mode="out-in">
        <router-link 
          v-if="currentBanner.link" 
          :to="currentBanner.link" 
          :key="currentIndex"
          class="banner-content"
        >
          <span class="banner-text">{{ currentBanner.text }}</span>
          <span class="banner-cta">
            자세히 보기
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </span>
        </router-link>
        <div v-else :key="currentIndex" class="banner-content">
          <span class="banner-text">{{ currentBanner.text }}</span>
        </div>
      </transition>

      <!-- 네비게이션 (2개 이상일 때) -->
      <div v-if="activeBanners.length > 1" class="banner-nav">
        <button class="nav-btn" @click="prevBanner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <span class="nav-count">{{ currentIndex + 1 }} / {{ activeBanners.length }}</span>
        <button class="nav-btn" @click="nextBanner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore'
import { db } from '@/services/firebase'

export default {
  name: 'SubBanner',
  setup() {
    const banners = ref([])
    const currentIndex = ref(0)
    let interval = null

    const activeBanners = computed(() => {
      return banners.value.filter(b => b.isActive)
    })

    const currentBanner = computed(() => {
      return activeBanners.value[currentIndex.value] || {}
    })

    const fetchBanners = async () => {
      try {
        const q = query(
          collection(db, 'subBanners'),
          where('isActive', '==', true),
          orderBy('order', 'asc')
        )
        const snapshot = await getDocs(q)
        banners.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('서브배너 로드 실패:', e)
      }
    }

    const nextBanner = () => {
      if (activeBanners.value.length > 1) {
        currentIndex.value = (currentIndex.value + 1) % activeBanners.value.length
        restartAutoPlay()
      }
    }

    const prevBanner = () => {
      if (activeBanners.value.length > 1) {
        currentIndex.value = currentIndex.value === 0 
          ? activeBanners.value.length - 1 
          : currentIndex.value - 1
        restartAutoPlay()
      }
    }

    const startAutoPlay = () => {
      interval = setInterval(() => {
        currentIndex.value = (currentIndex.value + 1) % activeBanners.value.length
      }, 5000)
    }

    const stopAutoPlay = () => {
      if (interval) clearInterval(interval)
    }

    const restartAutoPlay = () => {
      stopAutoPlay()
      startAutoPlay()
    }

    onMounted(async () => {
      await fetchBanners()
      if (activeBanners.value.length > 1) {
        startAutoPlay()
      }
    })

    onUnmounted(() => {
      stopAutoPlay()
    })

    return {
      activeBanners,
      currentBanner,
      currentIndex,
      nextBanner,
      prevBanner
    }
  }
}
</script>

<style scoped>
.sub-banner {
  padding: 16px 24px;
  transition: background 0.3s;
}

.banner-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #fff;
  text-decoration: none;
}

.banner-text {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.3px;
}

.banner-cta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  transition: background 0.2s;
}

a.banner-content:hover .banner-cta {
  background: rgba(255, 255, 255, 0.3);
}

/* 네비게이션 */
.banner-nav {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-btn {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.nav-count {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  min-width: 40px;
  text-align: center;
}

/* 페이드 애니메이션 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .sub-banner {
    padding: 14px 16px;
  }

  .banner-content {
    flex-direction: column;
    gap: 8px;
  }

  .banner-text {
    font-size: 14px;
    text-align: center;
  }

  .banner-cta {
    font-size: 13px;
    padding: 5px 10px;
  }

  .banner-nav {
    display: none;
  }
}
</style>